import {
    ColumnType,
    DropDown,
    getFilterElementsDefault,
    sharpDateFormatter,
    useWorkplaceState,
    WorkplaceBtnActions,
    WorkplaceBtnDelete,
} from '@finbeat/react-toolkit';
import { faBoltLightning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getSelectList } from 'axios/dictionaryApi';
import {
    deletePayment,
    getPaymentsExcel,
    getPaymentsList,
    getPaymentSourcesList,
} from 'axios/paymentApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import DataTimeText from 'components/Common/DataTime';
import Money from 'components/Common/Money';
import ButtonExcelFileUpload from 'components/ExcelFileUpload/ButtonExcelFileUpload';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { PaymentDto, PaymentFilter as PaymentFilterType, PaymentTable } from 'models/api/payment';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { getEndOfTheDay, getStartOfTheDay } from 'utils/dataTime';
import { UploadResult } from 'utils/fileUploadNotificator';
import PaymentsFilter from './PaymentsFilter';
import { AxiosError } from 'axios';
import { AxiosErrorResponse } from 'models/api/axiosErrorResponse';
import { dictValueFormatter } from 'utils/columnFormatters';

const Payments = () => {
    const api = useApi();
    const { t, i18n } = useTranslation();
    const queryClient = useQueryClient();
    const [paymentSources, setPaymentSources] = useState<DropDown[]>([]);
    const [statuses, setStatuses] = useState<DropDown[]>([]);
    const { hasRole } = useAuth();
    const { dateTimeFormat } = useGeneralSettingsContext();
    const { handleError } = useWorkplaceState();
    const { isFetching } = useQuery('PaymentSources', () => getPaymentSourcesList(api), {
        onSuccess: (data) => {
            setPaymentSources(data);
        },
        enabled: paymentSources.length === 0,
    });

    const { isFetching: isFetching2 } = useQuery(
        'PaymentStatuses',
        () => getSelectList(api, ['PaymentStatuses'], i18n.language),
        {
            onSuccess: (data) => {
                setStatuses(data['PaymentStatuses']);
            },
            enabled: statuses.length === 0,
        }
    );

    const { data: brandCodeResponse } = useQuery(
        'businessBrandCode',
        () => getSelectList(api, ['BusinessBrandCode']),
        {
            onError: (e: AxiosError<AxiosErrorResponse>) => handleError(e.response?.data),
        }
    );

    const businessBrandCode = useMemo<DropDown[]>(() => {
        return brandCodeResponse?.['BusinessBrandCode'] ?? [];
    }, [brandCodeResponse]);

    const uploadCallback = (res: UploadResult) => {
        res === 'success' && queryClient.invalidateQueries([['payments']]);
    };

    const initColumns: ColumnType<PaymentTable, PaymentTable[]>[] = [
        {
            key: 'orderId',
            name: <Trans i18nKey="BackOfficeAppId" />,
            width: 100,
            minWidth: 100,
        },
        { key: 'fio', name: <Trans i18nKey="BackOfficeFio" /> },
        { key: 'id', name: <Trans i18nKey="BackOfficeId" />, width: 50, minWidth: 50 },
        { key: 'docNum', name: <Trans i18nKey="BackOfficeDocNum" /> },
        { key: 'accountNo', name: <Trans i18nKey="BackOfficeAccount" /> },
        {
            key: 'createDate',
            name: <Trans i18nKey="BackOfficeCreateDt" />,
            formatter: ({ row }) => <DataTimeText value={row.createDate} />,
        },
        {
            key: 'dt',
            name: <Trans i18nKey="BackOfficePaymentDate" />,
            formatter: ({ row }) => <DataTimeText value={row.dt} />,
        },
        {
            key: 'amount',
            name: <Trans i18nKey="BackOfficeAmount" />,
            formatter: ({ row }) => <Money value={row.amount} />,
        },
        { key: 'sourceName', name: <Trans i18nKey="BackOfficeSource" /> },
        { key: 'referenceId', name: <Trans i18nKey="BackOfficeExternalCode" /> },
        {
            key: 'brandCode',
            name: <Trans i18nKey="BackOfficeBrandCode" />,
            formatter: dictValueFormatter('brandCode', businessBrandCode),
        },
        { key: 'dsc', name: <Trans i18nKey="BackOfficeComment" /> },
        { key: 'statusName', name: <Trans i18nKey="BackOfficeStatus" /> },
        {
            key: 'actions',
            name: <FontAwesomeIcon icon={faBoltLightning} />,
            cellClass: 'base-data-grid__cell_centered no-change-modal',
            headerCellClass: 'base-data-grid__cell_centered',
            width: 70,
            minWidth: 70,
            draggable: false,
            resizable: false,
            clickDisable: true,
            formatter: ({ row }) => {
                if (row.status !== -999) {
                    return (
                        <WorkplaceBtnActions>
                            {hasRole('paymentsDelete') && (
                                <WorkplaceBtnDelete key={`delete-btn-${row.id}`} id={row.id} />
                            )}
                        </WorkplaceBtnActions>
                    );
                }

                return <></>;
            },
        },
    ];

    return (
        <BOWorkplace<PaymentTable, PaymentDto, PaymentFilterType>
            pageTitle="BackOfficePayments"
            filterParams={{
                sd: getStartOfTheDay(),
                ed: getEndOfTheDay(),
                businessBrandCode: '',
                sourceId: null,
                status: null,
                referenceId: null,
                orderId: null,
            }}
            initColumns={initColumns}
            storagePath="payments/columns"
            getFilterElements={getFilterElementsDefault([
                {
                    id: 'sd',
                    formatter: (val) =>
                        `${t('BackOfficeDtFrom')}: ${sharpDateFormatter(val, dateTimeFormat)}`,
                },
                {
                    id: 'ed',
                    formatter: (val) =>
                        `${t('BackOfficeTo')}: ${sharpDateFormatter(val, dateTimeFormat)}`,
                },
                {
                    id: 'sourceId',
                    formatter: (val) => {
                        const source = paymentSources.find((i) => i.id === val.toString());
                        return source ? `${t('BackOfficeSource')}: ${source.name}` : '';
                    },
                },
                {
                    id: 'status',
                    formatter: (val) => {
                        const status = statuses.find((i) => i.id === val.toString());
                        return status ? `${t('BackOfficeStatus')}: ${status.name}` : '';
                    },
                },
                {
                    id: 'businessBrandCode',
                    formatter: (val) =>
                        businessBrandCode?.find((o) => o.id === val)?.name ?? val.toString(),
                },
                {
                    id: 'referenceId',
                    name: t('BackOfficeExternalCode'),
                },
                { id: 'orderId', name: t('BackOfficeAppId') },
            ])}
            filter={
                <PaymentsFilter
                    businessBrandCode={businessBrandCode}
                    paymentSources={paymentSources}
                    statuses={statuses}
                />
            }
            loadingFilter={isFetching || isFetching2}
            deleteMutationFn={(id) => deletePayment(api, id)}
            buttons={
                hasRole('paymentsUpload')
                    ? [
                          <ButtonExcelFileUpload
                              key="1"
                              controller="payments"
                              callback={uploadCallback}
                          />,
                      ]
                    : []
            }
            fetchQueryFn={(filter) => getPaymentsList(api, filter)}
            queryKey={['payments']}
            queryClient={queryClient}
            fetchExcelQueryFn={(filter) => getPaymentsExcel(api, filter)}
            showExcelBtn={hasRole('paymentsExcel')}
        />
    );
};

const usePaymentWorkplaceState = () => useWorkplaceState<PaymentTable, PaymentDto>();

export default Payments;
export { usePaymentWorkplaceState };
